import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import "../../../../styles/panel/user/manageShop/EditShopProducts.css";
import { API_BASE_URL } from "../../../../api/http";
import {
  BACK_ICON,
  CHEVRON_LEFT,
  CHEVRON_RIGHT,
  CLOSE_ICON,
  EDIT_ICON,
  EMPTY_ICON,
  IMAGE_ICON,
  PLUS_ICON,
  SEARCH_ICON,
  TRASH_ICON,
  VIEW_ICON,
  Icon,
} from "./shared/EditShopShared";
import {
  deleteProduct,
  getProducts,
} from "../../../../api/product_management/productApi";

import {
  swalConfirm,
  swalError,
  swalSuccess,
} from "../../../../utils/swalAlert";

const toFa = (n) => Number(n || 0).toLocaleString("fa-IR");

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

const resolveImageUrl = (src) =>
  src?.startsWith("http") ? src : `${API_BASE_URL}${src}`;

// خروجی رشته یا آبجکت با name/label/value رو به یه رشتهٔ قابل‌نمایش تبدیل می‌کنه
const normalizeLabel = (item) => {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  return item.name || item.label || item.title || item.value || "";
};

export default function EditShopProducts() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const onBack = () => navigate(-1);

  // جستجو، فیلتر، تعداد در صفحه و شماره‌ی صفحه توی query string آدرس نگه
  // داشته می‌شن؛ این‌طوری بعد از رفتن به صفحه‌ی ویرایش/افزودن و برگشتن
  // (navigate(-1))، همون صفحه‌ی قبلی (مثلاً ?page=4) دوباره باز می‌شه.
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("q") || "";
  const stockFilter = searchParams.get("stock") || "all"; // all | in | out
  const itemsPerPage = Number(searchParams.get("per")) || 10;
  const page = Number(searchParams.get("page")) || 1;

  // replace: true تا با هر تغییر، history پر نشه
  const updateParams = (updates) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([k, v]) => {
          if (v === "" || v === null || v === undefined) next.delete(k);
          else next.set(k, String(v));
        });
        return next;
      },
      { replace: true },
    );
  };

  // تغییر جستجو/فیلتر/تعداد در صفحه، خودش صفحه رو به ۱ برمی‌گردونه
  const setSearch = (v) => updateParams({ q: v, page: "" });
  const setStockFilter = (v) =>
    updateParams({ stock: v === "all" ? "" : v, page: "" });
  const setItemsPerPage = (v) => updateParams({ per: v, page: "" });
  const setPage = (v) => {
    const n = typeof v === "function" ? v(page) : v;
    updateParams({ page: n === 1 ? "" : n });
  };

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  const loadProducts = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      try {
        const res = await getProducts(shopId);
        setProducts(res.data?.results || res.data || []);
      } catch (err) {
        console.log(err);
        setProducts([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [shopId],
  );

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const filtered = useMemo(() => {
    let list = products;
    const q = search.trim();
    if (q) {
      list = list.filter((p) => p.name?.includes(q));
    }
    if (stockFilter === "in") {
      list = list.filter((p) => Number(p.stock) > 0);
    } else if (stockFilter === "out") {
      list = list.filter((p) => Number(p.stock) === 0);
    }
    return list;
  }, [products, search, stockFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleView = (product) => {
    setViewProduct(product);
    setActiveImage(0);
  };

  // به مسیر (URL) مستقلِ ویرایش/افزودن هدایت می‌کنیم؛ محصول در
  // EditShopProductPage از سرور گرفته می‌شه. آدرس فعلی (با ?page=...) توی
  // history می‌مونه و navigate(-1) دقیقاً همون رو برمی‌گردونه.
  const handleEdit = (product) => {
    navigate(`edit/${product.id}`);
  };

  const handleAdd = () => {
    navigate("add");
  };

  const handleDelete = async (product) => {
    const result = await swalConfirm({
      title: "حذف محصول",
      text: product.name
        ? `آیا از حذف محصول «${product.name}» مطمئن هستی؟`
        : "آیا از حذف این محصول مطمئن هستی؟",
      confirmText: "بله، حذف کن",
      cancelText: "انصراف",
      danger: true,
    });

    if (!result) return;

    try {
      setDeleting(true);
      await deleteProduct(shopId, product.id);

      setProducts((prev) => prev.filter((p) => p.id !== product.id));

      await swalSuccess({
        title: "delete",
        text: "محصول با موفقیت حذف شد",
      });
    } catch (err) {
      console.error(err);

      swalError({
        text: err?.response?.data?.detail || "خطایی در انجام عملیات رخ داد.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="shop-products">
      <div className="sp-head">
        <button className="sp-back-btn" onClick={onBack}>
          <Icon>{BACK_ICON}</Icon>
        </button>
        <div className="sp-head-title">
          <h1>محصولات</h1>
          <p>محصولات فروشگاهت رو اضافه، ویرایش یا حذف کن.</p>
        </div>
      </div>

      <div className="sp-toolbar">
        <div className="sp-search">
          <Icon>{SEARCH_ICON}</Icon>
          <input
            type="text"
            placeholder="جستجوی نام محصول..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="sp-filters">
          <select
            className="sp-select"
            value={stockFilter}
            onChange={(e) => setStockFilter(e.target.value)}
          >
            <option value="all">همه موجودی‌ها</option>
            <option value="in">موجود</option>
            <option value="out">ناموجود</option>
          </select>

          <button className="sp-back-btn sp-add-btn" onClick={handleAdd}>
            <Icon>{PLUS_ICON}</Icon>
            افزودن محصول
          </button>
        </div>
      </div>

      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead>
            <tr>
              <th>تصویر</th>
              <th>عنوان</th>
              <th>قیمت</th>
              <th>مبلغ تخفیف</th>
              <th>موجودی</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="sp-row-msg">
                <td colSpan={6}>در حال بارگذاری محصولات...</td>
              </tr>
            )}

            {!loading && pageItems.length === 0 && (
              <tr className="sp-row-msg">
                <td colSpan={6}>
                  <div className="sp-empty">
                    <span className="sp-empty-ic">
                      <Icon>{EMPTY_ICON}</Icon>
                    </span>
                    محصولی با این مشخصات پیدا نشد.
                  </div>
                </td>
              </tr>
            )}

            {!loading &&
              pageItems.map((p) => (
                <tr key={p.id}>
                  <td>
                    {(() => {
                      const coverImage = p.images?.find(
                        (img) => img.is_cover === true,
                      );

                      return coverImage ? (
                        <img
                          className="sp-thumb"
                          src={
                            coverImage.image.startsWith("http")
                              ? coverImage.image
                              : `${API_BASE_URL}${coverImage.image}`
                          }
                          alt="کاور محصول"
                        />
                      ) : (
                        <span className="sp-thumb sp-thumb-placeholder">
                          <Icon>{IMAGE_ICON}</Icon>
                        </span>
                      );
                    })()}
                  </td>
                  <td className="sp-title-cell">{p.name}</td>
                  <td>
                    <span className="sp-mono">{toFa(p.price)}</span> تومان
                  </td>
                  <td>
                    {p.discount_price ? (
                      <span className="sp-badge sp-badge--discount">
                        {toFa(p.discount_price)} تومان
                      </span>
                    ) : (
                      <span className="sp-dash">—</span>
                    )}
                  </td>
                  <td>
                    {Number(p.stock) > 0 ? (
                      <span className="sp-badge sp-badge--in">
                        {toFa(p.stock)} عدد
                      </span>
                    ) : (
                      <span className="sp-badge sp-badge--out">ناموجود</span>
                    )}
                  </td>
                  <td>
                    <div className="sp-row-actions">
                      <button
                        className="sp-icon-btn"
                        title="نمایش"
                        onClick={() => handleView(p)}
                      >
                        <Icon>{VIEW_ICON}</Icon>
                      </button>
                      <button
                        className="sp-icon-btn"
                        title="ویرایش"
                        onClick={() => handleEdit(p)}
                      >
                        <Icon>{EDIT_ICON}</Icon>
                      </button>
                      <button
                        className="sp-icon-btn sp-icon-btn--danger"
                        title="حذف"
                        onClick={() => handleDelete(p)}
                      >
                        <Icon>{TRASH_ICON}</Icon>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && filtered.length > 0 && (
        <div className="sp-pagination">
          <select
            className="sp-select sp-per-page-select"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {toFa(n)}
              </option>
            ))}
          </select>

          <span className="sp-page-info">
            صفحه {toFa(currentPage)} از {toFa(totalPages)} ·{" "}
            {toFa(filtered.length)} محصول
          </span>
          <div className="sp-page-controls">
            <button
              className="sp-page-btn sp-page-btn--nav"
              disabled={currentPage <= 1}
              onClick={() => setPage(Math.max(1, currentPage - 1))}
            >
              <Icon>{CHEVRON_RIGHT}</Icon>
            </button>
            {Array.from({ length: totalPages }).map((_, i) => {
              const n = i + 1;
              if (
                totalPages > 7 &&
                n !== 1 &&
                n !== totalPages &&
                Math.abs(n - currentPage) > 1
              ) {
                if (n === 2 || n === totalPages - 1) {
                  return (
                    <span className="sp-page-dots" key={n}>
                      …
                    </span>
                  );
                }
                return null;
              }
              return (
                <button
                  key={n}
                  className={`sp-page-btn ${
                    n === currentPage ? "sp-page-btn--active" : ""
                  }`}
                  onClick={() => setPage(n)}
                >
                  {toFa(n)}
                </button>
              );
            })}
            <span className="sp-page-current-mobile">{toFa(currentPage)}</span>
            <button
              className="sp-page-btn sp-page-btn--nav"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            >
              <Icon>{CHEVRON_LEFT}</Icon>
            </button>
          </div>
        </div>
      )}

      {viewProduct && (
        <div
          className="sp-overlay"
          onClick={(e) => e.target === e.currentTarget && setViewProduct(null)}
        >
          <div className="sp-view-modal">
            <button
              className="sp-view-close"
              onClick={() => setViewProduct(null)}
            >
              <Icon>{CLOSE_ICON}</Icon>
            </button>

            <h3 className="sp-view-title">{viewProduct.name}</h3>

            {(() => {
              const images = viewProduct.images?.length
                ? viewProduct.images
                : [];
              const active = images[activeImage] || images[0];

              return (
                <div className="sp-view-gallery">
                  {active ? (
                    <img
                      className="sp-view-main-img"
                      src={resolveImageUrl(active.image)}
                      alt={viewProduct.name}
                    />
                  ) : (
                    <span className="sp-view-main-img sp-view-main-img--empty">
                      <Icon>{IMAGE_ICON}</Icon>
                    </span>
                  )}

                  {images.length > 1 && (
                    <div className="sp-view-thumbs">
                      {images.map((img, i) => (
                        <button
                          key={img.id ?? i}
                          className={`sp-view-thumb ${
                            i === activeImage ? "sp-view-thumb--active" : ""
                          }`}
                          onClick={() => setActiveImage(i)}
                        >
                          <img
                            src={resolveImageUrl(img.image)}
                            alt={`${viewProduct.name} ${toFa(i + 1)}`}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="sp-view-grid">
              <div className="sp-view-field">
                <span className="sp-view-label">قیمت</span>
                <span className="sp-view-value">
                  <span className="sp-mono">{toFa(viewProduct.price)}</span>{" "}
                  تومان
                </span>
              </div>
              <div className="sp-view-field">
                <span className="sp-view-label">قیمت تخفیف</span>
                <span className="sp-view-value">
                  {viewProduct.discount_price ? (
                    <span className="sp-badge sp-badge--discount">
                      {toFa(viewProduct.discount_price)} تومان
                    </span>
                  ) : (
                    <span className="sp-dash">—</span>
                  )}
                </span>
              </div>
              <div className="sp-view-field">
                <span className="sp-view-label">موجودی</span>
                <span className="sp-view-value">
                  {Number(viewProduct.stock) > 0 ? (
                    <span className="sp-badge sp-badge--in">
                      {toFa(viewProduct.stock)} عدد
                    </span>
                  ) : (
                    <span className="sp-badge sp-badge--out">ناموجود</span>
                  )}
                </span>
              </div>
              <div className="sp-view-field">
                <span className="sp-view-label">واحد</span>
                <span className="sp-view-value">
                  {normalizeLabel(viewProduct.unit) || (
                    <span className="sp-dash">—</span>
                  )}
                </span>
              </div>
              <div className="sp-view-field">
                <span className="sp-view-label">دسته‌بندی</span>
                <span className="sp-view-value">
                  {normalizeLabel(viewProduct.category_name) || (
                    <span className="sp-dash">—</span>
                  )}
                </span>
              </div>
            </div>

            <div className="sp-view-section">
              <span className="sp-view-label">توضیحات</span>
              {viewProduct.description ? (
                <div
                  className="sp-view-desc"
                  // توضیحات از ApfDescriptionEditor به‌صورت HTML ذخیره می‌شه
                  // (مثلاً <p>...</p><p><strong>...</strong></p>)؛ چون این HTML
                  // از سمت فروشنده میاد، قبل از رندر حتماً sanitize می‌شه تا
                  // جلوی XSS گرفته بشه.
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(viewProduct.description),
                  }}
                />
              ) : (
                <p className="sp-view-desc">
                  <span className="sp-dash">توضیحی ثبت نشده</span>
                </p>
              )}
            </div>

            {!!(
              viewProduct.collections_detail?.length ||
              viewProduct.collections?.length
            ) && (
              <div className="sp-view-section">
                <span className="sp-view-label">ویترین‌ها</span>
                <div className="sp-view-chips">
                  {(
                    viewProduct.collections_detail ||
                    viewProduct.collections ||
                    []
                  ).map((c, i) => (
                    <span className="sp-chip" key={c.id ?? i}>
                      {normalizeLabel(c)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!!viewProduct.attributes?.length && (
              <div className="sp-view-section">
                <span className="sp-view-label">ویژگی‌ها</span>
                <div className="sp-view-chips">
                  {viewProduct.attributes.map((f, i) => {
                    const label = normalizeLabel(f);
                    const val = typeof f === "object" ? f.value : null;
                    return (
                      <span className="sp-chip" key={i}>
                        {val ? `${label}: ${val}` : label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {!!viewProduct.colors?.length && (
              <div className="sp-view-section">
                <span className="sp-view-label">رنگ‌ها</span>
                <div className="sp-view-colors">
                  {viewProduct.colors.map((c, i) => {
                    const hex = typeof c === "object" ? c.hex || c.code : null;
                    const label = normalizeLabel(c);
                    return (
                      <span className="sp-color-chip" key={i}>
                        {hex && (
                          <span
                            className="sp-color-dot"
                            style={{ background: hex }}
                          />
                        )}
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
